import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Sparkles, Linkedin, Mic, MessageCircle } from "lucide-react";
import { MaskedLines, Reveal, StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { EnquiryForm } from "@/components/shared/EnquiryForm";
import { useSeo } from "@/hooks/useSeo";
import {
  ASSETS,
  FOUNDER,
  WHATSAPP,
  TRUST_LINE,
  HERO_TAGS,
  HIGHLIGHTS,
  WHY_CHOOSE,
  EXPERT_TOPICS,
  JOURNEY,
} from "@/lib/site";
import { FEATURED } from "@/lib/programsData";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} />;
};

/* 1 — HERO */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacityBg = useTransform(scrollYProgress, [0, 0.9], [1, 0.4]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative min-h-[88vh] overflow-hidden bg-navy pt-28 md:min-h-[92vh] md:pt-36"
    >
      {/* ===== Background layer: image + parallax ===== */}
      <motion.div
        aria-hidden="true"
        style={{ y: yBg, scale: scaleBg, opacity: opacityBg }}
        className="pointer-events-none absolute inset-0"
      >
        <img
          src="/generated/hero-ai.jpg"
          alt=""
          loading="eager"
          fetchpriority="high"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* ===== Overlay layers: readability + brand glow ===== */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            // Dark navy gradient for text contrast + subtle royal + orange brand accents
            "linear-gradient(90deg, rgba(6,15,34,0.92) 0%, rgba(6,15,34,0.72) 45%, rgba(6,15,34,0.35) 100%), " +
            "radial-gradient(42% 55% at 12% 10%, hsla(224,76%,48%,0.28), transparent 70%), " +
            "radial-gradient(35% 40% at 92% 24%, hsla(21,100%,50%,0.14), transparent 72%)",
        }}
      />

      {/* Animated grid — subtle premium motion */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.22] mix-blend-screen"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 70% 50%, black 35%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at 70% 50%, black 35%, transparent 80%)",
        }}
      />

      {/* Scan line — slow cinematic sweep */}
      <motion.div
        aria-hidden="true"
        initial={{ y: "-100%" }}
        animate={{ y: "110%" }}
        transition={{ duration: 7, ease: "linear", repeat: Infinity, repeatDelay: 2.5 }}
        className="pointer-events-none absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-orange/10 to-transparent"
      />

      {/* Floating particles — pure CSS, zero JS cost */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        {[
          { l: "16%", t: "28%", d: 7, s: 1 },
          { l: "72%", t: "18%", d: 9, s: 1.5 },
          { l: "42%", t: "72%", d: 8, s: 1 },
          { l: "88%", t: "66%", d: 11, s: 2 },
          { l: "22%", t: "82%", d: 6, s: 1 },
          { l: "60%", t: "46%", d: 10, s: 1.2 },
        ].map((p, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 0.9, 0], y: [0, -40] }}
            transition={{ duration: p.d, delay: i * 0.9, repeat: Infinity, ease: "easeOut" }}
            style={{ left: p.l, top: p.t, width: `${p.s * 4}px`, height: `${p.s * 4}px` }}
            className="absolute rounded-full bg-orange shadow-[0_0_12px_#FF5A00]"
          />
        ))}
      </div>

      {/* ===== Foreground content ===== */}
      <div className="container-asai relative z-10 flex min-h-[inherit] flex-col justify-center gap-12 pb-20 pt-8 lg:gap-14 lg:pb-28">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium uppercase tracking-[0.16em] text-white/80 backdrop-blur-md"
          >
            <Sparkles className="h-3.5 w-3.5 text-orange" /> Atmasamman School of AI
          </motion.span>

          <h1 className="font-heading text-[2.6rem] font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-[4.6rem]">
            <MaskedLines lines={["Build Your Future with", "Artificial Intelligence"]} accentIndex={1} />
          </h1>

          <Reveal delay={0.6} className="mt-6 max-w-xl space-y-4 text-base text-white/70 md:text-lg">
            <p>
              Gain practical, industry-oriented skills in Artificial Intelligence, Generative AI,
              Machine Learning, Cloud Computing, DevOps, Digital Payments, UPI, SQL, Linux and
              Software Development.
            </p>
            <p>Learn from experienced industry professionals and work on real-world projects.</p>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <PrimaryButton
              href="https://app.atmasammanschoolofai.com/courses"
              target="_blank"
              rel="noopener noreferrer"
              withArrow
              testId="hero-explore-programs"
            >
              Explore Programs
            </PrimaryButton>
            <SecondaryButton to="/apply" onDark testId="hero-apply-internship">Apply for Internship</SecondaryButton>
            <SecondaryButton to="/contact" onDark testId="hero-talk-expert">Talk to a Career Expert</SecondaryButton>
          </motion.div>

          <Reveal delay={1} className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-white/60 md:text-sm">
            {TRUST_LINE.map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                {i > 0 && <span className="h-1 w-1 rounded-full bg-orange" />}
                {t}
              </span>
            ))}
          </Reveal>
        </div>

        {/* Course category chips — kept, now full-width under hero copy */}
        <div className="flex flex-wrap gap-2.5" data-testid="hero-course-chips">
          {HERO_TAGS.map((tag, i) => (
            <motion.span
              key={tag}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05 + i * 0.06, duration: 0.5 }}
              className="rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-xs font-medium text-white/85 backdrop-blur-md transition-colors hover:border-orange/60 hover:bg-orange/10 hover:text-white md:text-sm"
            >
              {tag}
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* 2 — TRUST & HIGHLIGHTS */
const Highlights = () => (
  <section className="bg-navy py-16 md:py-20" data-testid="home-highlights">
    <div className="container-asai">
      <StaggerGroup className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {HIGHLIGHTS.map((h) => (
          <StaggerItem key={h.title} className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center transition-colors hover:border-orange/40">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/15 text-orange">
              <Icon name={h.icon} className="h-5 w-5" />
            </span>
            <span className="text-sm font-medium leading-snug text-white/85">{h.title}</span>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

/* 3 — ABOUT ASAI */
const About = () => (
  <section className="bg-white py-24 md:py-32" data-testid="home-about">
    <div className="container-asai grid gap-14 lg:grid-cols-2 lg:items-center">
      <Reveal>
        <div className="overflow-hidden rounded-3xl border border-navy/10 bg-navy">
          <img
            src="/generated/about-ai-ml.png"
            alt="Illustration of an AI neural network with glowing nodes and data flow lines representing Artificial Intelligence and Machine Learning"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </Reveal>
      <div className="flex flex-col gap-6">
        <SectionHeading overline="About ASAI" title="Bridging the Gap Between Education and Industry" />
        <div className="space-y-4 text-navy/70">
          <p>
            ASAI – Atmasamman School of AI is an industry-focused education and career-development
            platform created to bridge the gap between academic learning and real-world industry
            expectations.
          </p>
          <p>
            We help students, graduates and working professionals develop practical skills in
            Artificial Intelligence, Generative AI, Machine Learning, Cloud Computing, DevOps, SQL,
            Linux, UPI, Digital Payments and Software Development.
          </p>
          <p>
            Our programs focus on practical learning, live projects, industry exposure, mentorship,
            communication skills, interview preparation and career readiness.
          </p>
        </div>
        <PrimaryButton to="/about" withArrow className="w-fit" testId="about-know-more">Know More About ASAI</PrimaryButton>
      </div>
    </div>
  </section>
);

/* 4 — WHY CHOOSE ASAI */
const WhyChoose = () => (
  <section className="bg-lightgrey py-24 md:py-32" data-testid="home-why-choose">
    <div className="container-asai">
      <SectionHeading overline="Why Choose ASAI" title="Everything you need to become industry-ready" description="A complete learning experience engineered around real outcomes." />
      <StaggerGroup className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {WHY_CHOOSE.map((w) => (
          <StaggerItem key={w.title} className="group flex flex-col gap-4 rounded-2xl border border-navy/10 bg-white p-6 transition-all hover:-translate-y-1 hover:border-royal/40 hover:shadow-[0_20px_50px_-30px_rgba(29,78,216,0.5)]">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 text-royal transition-colors group-hover:bg-orange group-hover:text-white">
              <Icon name={w.icon} className="h-5 w-5" />
            </span>
            <h3 className="font-heading text-lg leading-snug text-navy">{w.title}</h3>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

/* 5 — FEATURED PROGRAMS */
const ProgramItem = ({ p, i }) => (
  <StaggerItem
    className={`flex flex-col overflow-hidden rounded-2xl border p-7 ${
      p.highlight ? "border-orange/50 bg-navy-800" : "border-white/10 bg-white/[0.03]"
    }`}
  >
    {p.highlight && (
      <span className="mb-4 w-fit rounded-full bg-orange px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
        Most Popular
      </span>
    )}
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange">
      <Icons.Clock className="h-3.5 w-3.5" /> {p.duration}
    </div>
    <h3 className="mt-3 font-heading text-2xl leading-snug text-white">{p.name}</h3>
    <p className="mt-3 text-sm leading-relaxed text-white/65">{p.description}</p>
    <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-white/60">
      <span className="rounded-full border border-white/12 px-3 py-1">{p.mode}</span>
    </div>
    <div className="mt-5 border-t border-white/10 pt-5">
      <div className="font-heading text-xl text-white">{p.price}</div>
      <div className="text-xs text-white/50">{p.priceNote}</div>
    </div>
    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
      <SecondaryButton to={p.detailPath} onDark className="flex-1" testId={`program-view-${i}`}>View Details</SecondaryButton>
      <PrimaryButton to="/apply" className="flex-1" testId={`program-apply-${i}`}>Apply Now</PrimaryButton>
    </div>
  </StaggerItem>
);

const FeaturedPrograms = () => (
  <section className="bg-navy py-24 md:py-32" data-testid="home-programs">
    <div className="container-asai">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading onDark overline="Featured Programs" title="Programs built for careers" description="Choose the pathway that matches your goals and timeline." />
        <SecondaryButton to="/programs" onDark withArrow testId="programs-view-all">View all programs</SecondaryButton>
      </div>
      <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURED.map((p, i) => (
          <ProgramItem key={p.name} p={p} i={i} />
        ))}
      </StaggerGroup>
    </div>
  </section>
);

/* 6 — EXPERT SESSIONS FOR COLLEGES */
const ExpertSessions = () => (
  <section className="bg-white py-24 md:py-32" data-testid="home-expert-sessions">
    <div className="container-asai grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <div className="flex flex-col gap-6">
        <SectionHeading overline="For Colleges & Universities" title="Industry Expert Sessions for Colleges and Universities" description="Bring practitioners and current industry knowledge directly to your campus." />
        <div className="flex flex-wrap gap-3">
          <PrimaryButton to="/contact" withArrow testId="expert-invite">Invite an Expert</PrimaryButton>
          <SecondaryButton to="/college-partnerships" testId="expert-partner">Partner with ASAI</SecondaryButton>
        </div>
      </div>
      <StaggerGroup className="grid gap-4 sm:grid-cols-2">
        {EXPERT_TOPICS.map((t) => (
          <StaggerItem key={t} className="flex items-start gap-3 rounded-2xl border border-navy/10 bg-lightgrey p-5">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-royal/10 text-royal">
              <Icons.Check className="h-4 w-4" />
            </span>
            <span className="text-sm font-medium text-navy">{t}</span>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

/* 7 — STUDENT JOURNEY */
const Journey = () => (
  <section className="bg-lightgrey py-24 md:py-32" data-testid="home-journey">
    <div className="container-asai">
      <SectionHeading align="center" className="text-center" overline="Your Path" title="The ASAI Student Journey" description="A clear, guided path from first enquiry to a career-ready outcome." />
      <StaggerGroup className="mt-14 flex flex-wrap items-stretch justify-center gap-3" stagger={0.07}>
        {JOURNEY.map((step, i) => (
          <StaggerItem key={step} className="flex items-center gap-3">
            <div className="flex min-w-[150px] flex-col gap-2 rounded-2xl border border-navy/10 bg-white p-4">
              <span className="font-heading text-2xl font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm font-semibold text-navy">{step}</span>
            </div>
            {i < JOURNEY.length - 1 && <ArrowRight className="hidden h-5 w-5 shrink-0 text-orange lg:block" />}
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

/* 8 — FOUNDER PREVIEW */
const FounderPreview = () => (
  <section className="relative overflow-hidden bg-navy py-24 md:py-32" data-testid="home-founder">
    <div className="container-asai grid items-center gap-12 lg:grid-cols-[0.85fr_1.15fr]">
      <Reveal className="relative">
        <div className="relative overflow-hidden rounded-3xl border border-white/10" style={{ clipPath: "polygon(0 0, 100% 0, 100% 93%, 0 100%)" }}>
          <img src={ASSETS.founderDesk} alt={`${FOUNDER.name}, Founder & CEO of ASAI`} className="aspect-[4/5] w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
        </div>
        <div className="pointer-events-none absolute -bottom-6 -left-6 h-40 w-40 rounded-full bg-orange/20 blur-3xl" />
      </Reveal>
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-orange">Leadership</span>
        <h2 className="mt-4 font-heading text-4xl text-white md:text-5xl">{FOUNDER.name}</h2>
        <div className="mt-3 flex flex-col gap-0.5 text-white/70">
          {FOUNDER.titles.map((t) => (
            <span key={t} className="font-medium">{t}</span>
          ))}
        </div>
        <p className="mt-6 max-w-xl text-white/65">{FOUNDER.profile}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryButton to="/founder" withArrow testId="founder-view-profile">View Founder Profile</PrimaryButton>
          <SecondaryButton href={FOUNDER.linkedin} onDark testId="founder-linkedin" target="_blank" rel="noopener noreferrer">
            <Linkedin className="h-4 w-4" /> Connect on LinkedIn
          </SecondaryButton>
          <SecondaryButton to="/contact" onDark testId="founder-invite-speaker">
            <Mic className="h-4 w-4" /> Invite as Speaker
          </SecondaryButton>
        </div>
      </div>
    </div>
  </section>
);

/* 10 — FINAL CTA */
const FinalCTA = () => (
  <section className="relative overflow-hidden py-24 md:py-28" data-testid="home-cta">
    <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, hsl(224 76% 40%), hsl(21 100% 50%))" }} />
    <div className="container-asai relative z-10 flex flex-col items-center gap-8 text-center">
      <Reveal>
        <h2 className="font-heading text-4xl font-bold leading-tight text-white md:text-6xl">
          Start Building Your Industry-Ready Career
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="flex flex-wrap justify-center gap-4">
        <PrimaryButton to="/apply" size="lg" className="bg-white !text-navy hover:!bg-white" withArrow testId="cta-apply">Apply Now</PrimaryButton>
        <a href="/contact" data-testid="cta-counselling" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-base font-medium text-white transition-colors hover:bg-white/10">
          Book Free Counselling
        </a>
        <a href={WHATSAPP.link} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-base font-medium text-white transition-colors hover:bg-white/10">
          <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
        </a>
      </Reveal>
    </div>
  </section>
);

const QuickEnquiry = () => (
  <section className="bg-navy py-24 md:py-32" data-testid="home-enquiry">
    <div className="container-asai grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
      <SectionHeading onDark overline="Quick Enquiry" title="Have a question? Request a callback" description="Share a few details and our team will reach out to guide you." />
      <EnquiryForm onDark source="home" testId="home-enquiry-form" />
    </div>
  </section>
);

export default function Home() {
  useSeo({
    title: undefined, // uses full default site title
    description:
      "ASAI – Atmasamman School of AI. Industry-focused AI programs and internships in Bulandshahr, Uttar Pradesh. Learn Artificial Intelligence, Generative AI, Machine Learning, Cloud, DevOps, UPI and Digital Payments — with live projects and career support.",
    path: "/",
    breadcrumbs: [{ name: "Home", path: "/" }],
  });
  return (
    <div data-testid="page-home">
      <Hero />
      <Highlights />
      <About />
      <WhyChoose />
      <ExpertSessions />
      <Journey />
      <FounderPreview />
      <QuickEnquiry />
      <FinalCTA />
    </div>
  );
}
